import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LiveProgram } from '@/mocks/livePrograms';
import { SwalAlert } from '@/hooks/useSweetAlert';

interface ProgramLiveCardProps {
  program: LiveProgram;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

export default function ProgramLiveCard({ program }: ProgramLiveCardProps) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handleEnter = useCallback(() => {
    navigate(`/program/${program.slug}`);
  }, [navigate, program.slug]);

  const handleShare = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/program/${program.slug}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: program.name, text: `Escucha ${program.name} en ${program.radioName}`, url: shareUrl });
      } catch { /* cancel */ }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      SwalAlert.toast('Enlace copiado al portapapeles', 'success');
    }
  }, [program.name, program.radioName, program.slug]);

  return (
    <article
      className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/80 hover:border-foreground-300/50 transition-all duration-300 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Cover Image + Overlay */}
      <div className="relative aspect-[16/10] overflow-hidden" onClick={handleEnter}>
        <img
          src={program.coverUrl}
          alt={program.name}
          className={`w-full h-full object-cover object-top transition-transform duration-500 ${isHovered ? 'scale-105' : 'scale-100'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10"></div>

        {/* LIVE Badge */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
          <span className="text-[10px] font-extrabold text-background-50 tracking-widest uppercase bg-red-500/90 px-2.5 py-1 rounded-full backdrop-blur-sm">
            AL AIRE
          </span>
        </div>

        {/* Category Badge */}
        <div className="absolute top-3 right-3">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-background-50 bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
            {program.category}
          </span>
        </div>

        {/* UpTime */}
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
          <i className="ri-time-line text-[10px] text-background-50/80"></i>
          <span className="text-[10px] font-semibold text-background-50/80">{program.upTime} al aire</span>
        </div>
      </div>

      {/* Info Section */}
      <div className="p-4">
        {/* Program Name */}
        <h3
          className="text-base font-extrabold text-foreground-950 leading-tight mb-1 group-hover:text-primary-600 transition-colors line-clamp-1"
          onClick={handleEnter}
        >
          {program.name}
        </h3>

        {/* Radio */}
        <div className="flex items-center gap-1.5 mb-2">
          <i className="ri-radio-line text-[11px] text-foreground-400 shrink-0 w-3.5 h-3.5 flex items-center justify-center"></i>
          <span className="text-[11px] font-semibold text-foreground-500 truncate">
            {program.radioName}
            <span className="text-foreground-300 font-medium"> · {program.radioFrequency}</span>
          </span>
        </div>

        {/* Host */}
        <div className="flex items-center gap-2 mb-3 pt-2 border-t border-background-200/60">
          <div className="w-8 h-8 rounded-full bg-background-200 overflow-hidden shrink-0">
            <img src={program.hostPhotoUrl} alt={program.host} className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-bold text-foreground-800 truncate">{program.host}</p>
            <p className="text-[10px] font-medium text-foreground-400">Locutor</p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="flex items-center gap-4 mb-4 pt-3 border-t border-background-200/60">
          <div className="flex items-center gap-1.5">
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
              <i className="ri-group-line text-xs text-foreground-500"></i>
            </div>
            <div>
              <p className="text-xs font-extrabold text-foreground-950 leading-none">{formatNumber(program.followers)}</p>
              <p className="text-[9px] font-semibold text-foreground-400">Seguidores</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
              <i className="ri-chat-smile-2-line text-xs text-foreground-500"></i>
            </div>
            <div>
              <p className="text-xs font-extrabold text-foreground-950 leading-none">{program.interactions}</p>
              <p className="text-[9px] font-semibold text-foreground-400">Interacciones</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleEnter}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-xs font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-circle-line text-sm"></i>
            Ir al Programa
          </button>
          <button
            onClick={handleShare}
            className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-600 transition-all shrink-0"
            title="Compartir"
          >
            <i className="ri-share-forward-line text-sm"></i>
          </button>
        </div>
      </div>
    </article>
  );
}